// src/routes.tsx

import { createBrowserRouter } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Login } from '@/pages/Auth/Login';
import { Register } from '@/pages/Auth/Register';
import { Overview } from '@/pages/Dashboard/Overview';
import { Profile } from '@/pages/Developer/Profile';
import { ChangePassword } from '@/pages/Developer/ChangePassword';
import { CategoryList } from '@/pages/Categories/CategoryList';
import { CategoryDetail } from '@/pages/Categories/CategoryDetail';
import { ProductList } from '@/pages/Products/ProductList';
import { ProductCreate } from '@/pages/Products/ProductCreate';
import { ProductDetail } from '@/pages/Products/ProductDetail';
import { ProductEdit } from '@/pages/Products/ProductEdit';
import { ProductImageUpload } from '@/pages/ProductImages/ProductImageUpload';
import { ProductImageList } from '@/pages/ProductImages/ProductImageList';
import { ProductOTAList } from '@/pages/Products/ProductOTAList';      // ✅ 新增
import { OTAUpload } from '@/pages/Products/OTAUpload';                // ✅ 新增
import { OTAUpgrade } from '@/pages/Products/OTAUpgrade';              // ✅ 新增
import { AppList } from '@/pages/Apps/AppList';
import { AppCreate } from '@/pages/Apps/AppCreate';
import { AppDetail } from '@/pages/Apps/AppDetail';
import { AppKeysManagement } from '@/pages/Apps/AppKeysManagement';
import { AppProductsBinding } from '@/pages/Apps/AppProductsBinding';
import { AppEdit } from '@/pages/Apps/AppEdit';
import { PanelList } from '@/pages/Panels/PanelList';
import { PanelUpload } from '@/pages/Panels/PanelUpload';
import { PanelDetail } from '@/pages/Panels/PanelDetail';
import { PanelUpdate } from '@/pages/Panels/PanelUpdate';
import { ImageUpload as PanelImageUpload } from '@/pages/PanelImages/ImageUpload';
import { ImageList as PanelImageList } from '@/pages/PanelImages/ImageList';
import { ProtectedRoute } from '@/components/ui/ProtectedRoute';
import { ForgotPassword } from './pages/Auth/ForgotPassword';
import { DeleteAccount } from '@/pages/Developer/DeleteAccount';

/**
 * 应用路由配置
 */
export const router = createBrowserRouter([
  // 认证路由（无需登录）
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/register',
    element: <Register />,
  },
  // 忘记密码路由
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  // 受保护路由（需要登录）
  {
    path: '/',
    element: <Layout />,
    children: [
      // 概况
      {
        index: true,
        element: <ProtectedRoute><Overview /></ProtectedRoute>,
      },
      // 开发者管理
      {
        path: 'profile',
        element: <ProtectedRoute><Profile /></ProtectedRoute>,
      },
      {
        path: 'password',
        element: <ProtectedRoute><ChangePassword /></ProtectedRoute>,
      },

      // 类别管理
      {
        path: 'categories',
        element: <ProtectedRoute><CategoryList /></ProtectedRoute>,
      },
      {
        path: 'categories/:categoryId',
        element: <ProtectedRoute><CategoryDetail /></ProtectedRoute>,
      },

      // 产品管理
      {
        path: 'products',
        element: <ProtectedRoute><ProductList /></ProtectedRoute>,
      },
      {
        path: 'products/create',
        element: <ProtectedRoute><ProductCreate /></ProtectedRoute>,
      },
      {
        path: 'products/:productId',
        element: <ProtectedRoute><ProductDetail /></ProtectedRoute>,
      },
      {
        path: 'products/:productId/edit',
        element: <ProtectedRoute><ProductEdit /></ProtectedRoute>,
      },
      // 产品图片管理
      {
        path: 'products/:productId/images',
        element: <ProtectedRoute><ProductImageList /></ProtectedRoute>,
      },
      {
        path: 'product-images/upload',
        element: <ProtectedRoute><ProductImageUpload /></ProtectedRoute>,
      },
      // ✅ 新增：OTA 固件管理
      {
        path: 'products/:productId/ota',
        element: <ProtectedRoute><ProductOTAList /></ProtectedRoute>,
      },
      {
        path: 'products/:productId/ota/upload',
        element: <ProtectedRoute><OTAUpload /></ProtectedRoute>,
      },
      {
        path: 'products/:productId/ota/upgrade',
        element: <ProtectedRoute><OTAUpgrade /></ProtectedRoute>,
      },

      // App SDK 管理
      {
        path: 'apps',
        element: <ProtectedRoute><AppList /></ProtectedRoute>,
      },
      {
        path: 'apps/create',
        element: <ProtectedRoute><AppCreate /></ProtectedRoute>,
      },
      {
        path: 'apps/:appId',
        element: <ProtectedRoute><AppDetail /></ProtectedRoute>,
      },
      {
        path: 'apps/:appId/edit',
        element: <ProtectedRoute><AppEdit /></ProtectedRoute>,
      },
      {
        path: 'apps/:appId/keys',
        element: <ProtectedRoute><AppKeysManagement /></ProtectedRoute>,
      },
      {
        path: 'apps/:appId/products',
        element: <ProtectedRoute><AppProductsBinding /></ProtectedRoute>,
      },

      // 面板管理
      {
        path: 'panels',
        element: <ProtectedRoute><PanelList /></ProtectedRoute>,
      },
      {
        path: 'panels/upload',
        element: <ProtectedRoute><PanelUpload /></ProtectedRoute>,
      },
      {
        path: 'panels/:panelId',
        element: <ProtectedRoute><PanelDetail /></ProtectedRoute>,
      },
      {
        path: 'panels/:panelId/edit',
        element: <ProtectedRoute><PanelUpdate /></ProtectedRoute>,
      },
      {
        path: 'panels/:panelId/update',
        element: <ProtectedRoute><PanelUpdate /></ProtectedRoute>,
      },

      // 面板图片管理
      {
        path: 'panel-images/upload',
        element: <ProtectedRoute><PanelImageUpload /></ProtectedRoute>,
      },
      {
        path: 'panels/:panelId/images',
        element: <ProtectedRoute><PanelImageList /></ProtectedRoute>,
      },
      // 注销账号
      {
        path: 'settings/delete-account',
        element: <ProtectedRoute><DeleteAccount /></ProtectedRoute>,
      },
    ],
  },
  // 404 页面（可选）
  {
    path: '*',
    element: (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
        <p className="text-gray-600 mb-8">页面未找到</p>
        <a href="/" className="text-blue-600 hover:underline">返回首页</a>
      </div>
    ),
  },
]);